"use client";

import React, { useState } from "react";
import { FaChevronDown } from "react-icons/fa";

export interface Card {
  indicator?: string;
  title?: string;
  paragraph1?: string;
  paragraph2?: string;
  paragraph3?: string;
}

export interface ThreeColumnCardSectionProps {
  title?: string;
  subtitle?: string;
  cards?: Card[];
}

/**
 * Three Column Card Section
 * Displays a section with title, subtitle, and three cards in a row.
 * Each card has a bold title, a down-arrow to expand/collapse, and when expanded
 * shows Paragraph 1, Paragraph 2, and Paragraph 3 (italic).
 */
export default function ThreeColumnCardSection({
  title,
  subtitle,
  cards = [],
}: ThreeColumnCardSectionProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const displayCards = cards.slice(0, 3);

  if (displayCards.length === 0) {
    return null;
  }

  const handleToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const hasContent = (card: Card) =>
    Boolean(card.paragraph1 || card.paragraph2 || card.paragraph3);

  return (
    <section className="w-full py-12">
      <div className="mx-auto w-full max-w-6xl px-4 md:max-w-7xl lg:max-w-[1400px] xl:max-w-[1600px] lg:px-8">
        {title && (
          <h2 className="mb-2 text-center text-3xl font-bold text-violet-950 md:text-4xl">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="mx-auto mb-8 max-w-6xl text-center text-lg text-gray-600">
            {subtitle}
          </p>
        )}

        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {displayCards.map((card, index) => {
            const isExpanded = expandedIndex === index;
            const showArrow = hasContent(card);

            return (
              <div
                key={index}
                className="relative flex flex-col rounded-3xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md md:p-8"
              >
                {card.indicator && (
                  <div className="absolute -top-4 left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border-2 border-violet-200 bg-white text-base font-bold text-violet-950 shadow-md">
                    {card.indicator}
                  </div>
              )}

              <div className="pt-4">
                  <div className="flex flex-col items-center gap-2 text-center">
                    {card.title && (
                      <h3 className="text-xl font-bold text-violet-950 md:text-2xl">
                        {card.title}
                      </h3>
                    )}
                    {showArrow && (
                      <button
                        type="button"
                        onClick={() => handleToggle(index)}
                        className="flex items-center justify-center rounded-full p-1.5 text-violet-950 transition-colors hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-2"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? "Collapse card" : "Expand card"}
                      >
                        <FaChevronDown
                          className={`size-5 transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                          aria-hidden
                        />
                      </button>
                    )}
                  </div>

                  {showArrow && (
                    <div
                      className="overflow-hidden transition-all duration-200 ease-in-out"
                      style={{
                        maxHeight: isExpanded ? 2000 : 0,
                        opacity: isExpanded ? 1 : 0,
                      }}
                    >
                      <div className="space-y-4 pt-4 text-gray-700">
                        {card.paragraph1 && (
                          <p className="text-base leading-relaxed">
                            {card.paragraph1}
                          </p>
                        )}
                        {card.paragraph2 && (
                          <p className="text-base leading-relaxed">
                            {card.paragraph2}
                          </p>
                        )}
                        {card.paragraph3 && (
                          <p className="text-base italic leading-relaxed text-gray-600">
                            {card.paragraph3}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
